<template>
 <div class="LogIn">
    <v-alert v-if="fromSign" color="secondary lighten-2" class="mb-8 secondary--text">
      <template v-slot:prepend><v-icon color="success" left>mdi-check-circle</v-icon></template>
      {{ $t('logIn.accountCreated') }}
    </v-alert>
    <div class="LogIn-title">{{ $t('logIn.title') }}</div>
    <div class="LogIn-subtitle">{{ $t('logIn.subtitle') }}</div>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(login)">
        <TextFieldValidation
          v-model="email"
          data-e2e="loginEmailInput"
          name="email"
          type="text"
          :label="$t('logIn.emailLabel')"
          rules="required|email"
          :placeholder="autofillEmail ? ` ` : null"
        />
        <ValidationProvider name="password" rules="required" v-slot="{ errors }">
          <v-text-field
            v-model="password"
            @click:append="showPassword = !showPassword"
            @input="$emit('change', $event)"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            data-e2e="loginPasswordInput"
            :error-messages="errors"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="autofillPassword ? ` ` : null"
          >
            <template v-slot:label>
              <span class="LogIn-passwordLabel">{{ $t('logIn.passwordLabel') }}</span>
            </template>
          </v-text-field>
        </ValidationProvider>
        <div class="LogIn-forgotPasswordLink">
          <span @click="submitForgotPassword">{{ $t('logIn.forgotPasswordButton') }}</span>
        </div>
        <v-btn
          type="submit"
          class="LogIn-submitButton mt-6 mb-3 text-none"
          color="primary"
          data-e2e="loginSubmitButton"
          depressed
          >{{ $t('logIn.submitButton') }}</v-btn
        >
        <v-alert v-if="error" color="error" data-e2e="loginErrorMessage" type="error" text dense>{{ error }}</v-alert>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import gql from 'graphql-tag'

import TextFieldValidation from '@/components/lib/inputValidation/textFieldValidation'
import auth from '@/services/auth'
import { initVueGtm } from '@/services/gtm'

const GET_COUNTRIES = gql`
  query myUser {
    myUser {
      shops {
        country
      }
    }
  }
`

const GET_MY_USER = gql`
  query getMyUser {
    myUser {
      shops {
        shopUuid
      }
    }
  }
`

export default {
  name: 'LogIn',

  components: { ValidationObserver, ValidationProvider, TextFieldValidation },

  props: {
    fromSign: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      email: '',
      error: false,
      password: '',
      showPassword: false,
      autofillEmail: false,
      autofillPassword: false
    }
  },

  mounted() {
    // search for autofill every 100ms
    this.intervalDetectAutofill = setInterval(() => {
      if (document.querySelectorAll('input[type="password"]:-webkit-autofill').length > 0) {
        this.autofillPassword = true
      }
      if (document.querySelectorAll('input[type="text"]:-webkit-autofill').length > 0) {
        this.autofillEmail = true
      }
    }, 100)

    // clean interval if needed after 3s
    setTimeout(() => {
      if (this.intervalDetectAutofill) {
        clearInterval(this.intervalDetectAutofill)
        this.intervalDetectAutofill = null
      }
    }, 3000)
  },

  methods: {
    async login() {
      try {
        const { ecpId } = await auth.login(this.email, this.password)

        if (!this.$gtm) {
          const { data } = await this.$apollo.query({ query: GET_COUNTRIES })
          if (data.myUser.shops[0]) {
            initVueGtm(data.myUser.shops[0].country)
          }
        }

        const userResponse = await this.$apollo.query({ query: GET_MY_USER })

        this.$gtm &&
          this.$gtm.trackEvent({
            event: 'Sign_in',
            essilor_client_id: ecpId,
            practitioner_id: ecpId,
            shopUuid: userResponse.data.myUser.shops[0] ? userResponse.data.myUser.shops[0].shopUuid : ''
          })

        await this.$router.push({ name: 'Home' })
      } catch (error) {
        this.error = this.$t('logIn.errorLogin')
      }
    },
    async submitForgotPassword() {
      this.error = false
      if (!this.isEmail()) {
        if (this.email === '') {
          this.error = this.$t('logIn.errorResetEmail')
        }
      } else {
        const url = `https://${window.location.host}/auth/reset-password`
        try {
          const request = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: this.email
            })
          })
          const response = await request.json()
          !response.status
            ? (this.error = this.$t('logIn.errorGeneric'))
            : await this.$router.push({ name: 'ResetPassword', params: { mode: 'emailSent' } })
        } catch (err) {
          this.error = this.$t('logIn.errorGeneric')
        }
      }
    },
    isEmail() {
      const str = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return str.test(this.email.toLowerCase())
    }
  }
}
</script>

<style scoped lang="scss">
.LogIn {
  position: relative;
  text-align: left;
  color: var(--v-secondary-base);
}

.LogIn-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.LogIn-subtitle {
  color: var(--v-secondary-base);
  font-weight: 300;
  margin-bottom: 40px;
}

.LogIn-forgotPasswordLink {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  span {
    color: var(--v-text-base);
    cursor: pointer;
    text-decoration: underline;
    &:hover {
      color: var(--v-text-lighten1);
    }
  }
}

.LogIn-submitButton {
  display: block;
  margin: 0 auto;
}

.LogIn-createAccountButton {
  display: block;
  margin-top: 3em;
  margin-left: auto;
}
.LogIn-passwordLabel {
  font-weight: 700;
  color: var(--v-secondary-base);
}
</style>
